import { put, select, takeLatest, fork } from 'redux-saga/effects'
import values from 'lodash/values'

import { createApiCall } from 'services/api'
import { ActionType, IPatchClient, IPatchClientAccountManager, IPostClient } from './model'
import { ActionCallback, IAxiosResponse } from '../model'
import { ActionType as CommonActionType } from '../common/model'
import { ActionType as AuthActionType } from '../authentication/model'
import { withTags } from '@centra/centra-ui'

export function* getClientsSaga(): Generator {
  yield takeLatest(ActionType.GET_CLIENTS, function* () {
    try {
      const {
        auth: { user },
      } = yield select()
      const { data, status }: IAxiosResponse = yield createApiCall({
        url: `/agency/${user.agencyId}/client?view=list&limit=1000`,
      })

      yield put({
        type: ActionType.SET_CLIENTS,
        payload: status === 200 ? data.items : null,
      })
    } catch (error) {
      // error
    }
  })
}

export function* getCentraClientsSaga(): Generator {
  yield takeLatest(ActionType.GET_CENTRA_CLIENTS, function* () {
    try {
      const {
        auth: { user },
      } = yield select()

      const { data, status }: IAxiosResponse = yield createApiCall({
        url: withTags({ user, tags: 'role.account-manager' }) ? `/clients/account-manager` : `clients/all`,
      })

      yield put({
        type: ActionType.SET_CENTRA_CLIENTS,
        payload: status === 200 ? data.items : null,
      })
    } catch (error) {
      // error
    }
  })
}

export function* getClientsOptionsSaga(): Generator {
  yield takeLatest(ActionType.GET_CLIENTS_OPTIONS, function* () {
    try {
      const {
        auth: { user },
      } = yield select()
      const { data, status }: IAxiosResponse = yield createApiCall({
        url: `/agency/${user.agencyId}/client?view=dict&limit=1000`,
      })

      yield put({
        type: ActionType.SET_CLIENTS_OPTIONS,
        payload:
          status === 200
            ? data.items.map((item: any) => ({
                value: item.id,
                label: item.name,
              }))
            : [],
      })
    } catch (error) {
      // error
    }
  })
}

export function* getAccountManagersSaga(): Generator {
  yield takeLatest(ActionType.GET_ACCOUNT_MANAGERS, function* () {
    try {
      const { data, status }: IAxiosResponse = yield createApiCall({
        url: `/account/account-managers`,
      })

      yield put({
        type: ActionType.SET_ACCOUNT_MANAGERS,
        payload: status === 200 ? data : [],
      })
    } catch (error) {
      // error
    }
  })
}

export function* getClientSaga(): Generator {
  yield takeLatest(ActionType.GET_CLIENT, function* (action: ActionCallback<{ id: string | number }>) {
    try {
      const { payload, callback } = action

      const {
        auth: { user },
      } = yield select()
      const { data, status }: IAxiosResponse = yield createApiCall({
        url: `/agency/${user.agencyId}/client/${payload.id}`,
      })

      yield put({
        type: ActionType.SET_CLIENT,
        payload: status === 200 ? data : null,
      })

      callback({ success: status === 200 })

      if (status === 400) {
        yield put({
          type: CommonActionType.SET_NOTIFICATION,
          payload: {
            status: 'error',
            message: values(data.fields)[0],
          },
        })
      }
    } catch (error) {
      // error
    }
  })
}

export function* postClientSaga(): Generator {
  yield takeLatest(ActionType.POST_CLIENT, function* (action: ActionCallback<IPostClient>) {
    try {
      const { payload, callback } = action
      const { logo, ...rest } = payload
      const reqObj: IPostClient = rest

      if (logo?.tmp) {
        reqObj.logo = logo.tmp.name || logo.tmp
      }

      const {
        auth: { user },
      } = yield select()
      const { data, status }: IAxiosResponse = yield createApiCall({
        method: 'POST',
        url: `/agency/${user.agencyId}/client`,
        data: reqObj,
      })

      if (status === 200) {
        callback({ success: true })
        yield put({ type: ActionType.GET_CLIENTS })
        yield put({
          type: CommonActionType.SET_NOTIFICATION,
          payload: {
            status: 'success',
            title: 'Клиент добавлен',
            message: `Клиент ${rest.name} успешно создан`,
          },
        })
      } else if (status === 400) {
        callback({ fields: data.fields })
      } else {
        callback({ success: false })
      }
    } catch (error) {
      // error
    }
  })
}

export function* patchClientSaga(): Generator {
  yield takeLatest(ActionType.PATCH_CLIENT, function* (action: ActionCallback<IPatchClient>) {
    try {
      const { payload, callback } = action
      const {
        clientId,
        data: { logo, ...rest },
      } = payload
      const reqObj: IPostClient = rest

      if (logo?.tmp) {
        reqObj.logo = logo.tmp.name || logo.tmp
      }

      const {
        auth: { user },
      } = yield select()

      if (withTags({ user, tags: ['role.agency-primary-manager', 'role.agency-manager'] })) {
        const agencyClientRes: IAxiosResponse = yield createApiCall({
          method: 'PATCH',
          url: `/agency/${user.agencyId}/client/${clientId}`,
          data: reqObj,
        })
        if (agencyClientRes.status === 200) {
          callback({ success: true })
          yield put({ type: ActionType.GET_CLIENTS })
          yield put({
            type: ActionType.GET_CLIENT,
            payload: {
              id: clientId,
            },
          })
        } else if (agencyClientRes.status === 400) {
          callback({ success: false, fields: agencyClientRes.data.fields })
        } else {
          callback({ success: false })
        }
        return
      }

      if (withTags({ user, tags: ['role.client-primary-manager', 'role.client-manager'] })) {
        const clientRes: IAxiosResponse = yield createApiCall({
          method: 'PATCH',
          url: `/clients/${clientId}`,
          data: reqObj,
        })
        if (clientRes.status === 200) {
          callback({ success: true })
          yield put({
            type: AuthActionType.GET_CLIENT_COMPANY,
            payload: {
              clientCompanyId: clientId,
            },
          })
        } else if (clientRes.status === 400) {
          callback({ success: false, fields: clientRes.data.fields })
        } else {
          callback({ success: false })
        }
        return
      }
    } catch (error) {
      // error
    }
  })
}

export function* patchAccountManagerSaga(): Generator {
  yield takeLatest(
    ActionType.PATCH_CLIENT_ACCOUNT_MANAGER,
    function* (action: ActionCallback<IPatchClientAccountManager>) {
      try {
        const { payload, callback } = action

        const { status }: IAxiosResponse = yield createApiCall({
          method: 'PATCH',
          url: `/account/${payload.clientId}/account-manager`,
          data: {
            accountManagerId: payload.accountManagerId,
            isAgency: payload.isAgency,
          },
        })

        yield put({
          type: ActionType.GET_CENTRA_CLIENTS,
        })

        callback({ success: status === 200 })
      } catch (error) {
        // error
      }
    }
  )
}

export default [
  fork(postClientSaga),
  fork(getClientsSaga),
  fork(getCentraClientsSaga),
  fork(getClientsOptionsSaga),
  fork(getClientSaga),
  fork(patchClientSaga),
  fork(getAccountManagersSaga),
  fork(patchAccountManagerSaga),
]
